import React, { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Box, TextField, Typography, useTheme } from '@mui/material';

interface CodeInputFieldProps {
  /** The current code value */
  value: string;
  /** Callback when code changes */
  onChange: (code: string) => void;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether to show an error state */
  error?: boolean;
  /** Error message to display */
  helperText?: string;
  /** Number of digits for the code */
  length?: number;
  /** Auto-focus the first input */
  autoFocus?: boolean;
  /** Called when all digits are entered */
  onComplete?: (code: string) => void;
  /** Label for accessibility */
  label?: string;
}

/**
 * A clean, accessible component for entering magic codes with individual digit inputs
 */
export const CodeInputField: React.FC<CodeInputFieldProps> = ({
  value = '',
  onChange,
  disabled = false,
  error = false,
  helperText,
  length = 6,
  autoFocus = false,
  onComplete,
  label = 'Verification Code',
}) => {
  const theme = useTheme();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  // Ensure we have the right number of input refs
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  // Auto-focus first input if requested
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  // Trigger onComplete when code is full - use a more stable approach
  const onCompleteRef = useRef(onComplete);
  const isCompleteRef = useRef(false);

  // Update the ref when onComplete changes
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const isComplete = value.length === length;

    // Only call onComplete if we just became complete (not already complete)
    if (isComplete && !isCompleteRef.current && onCompleteRef.current) {
      isCompleteRef.current = true;
      onCompleteRef.current(value);
    } else if (!isComplete) {
      isCompleteRef.current = false;
    }
  }, [value, length]);

  const handleInputChange = (index: number, inputValue: string) => {
    try {
      // Allow alphanumeric characters (letters and digits)
      const char = inputValue.replace(/[^a-zA-Z0-9]/g, '').slice(-1);

      // Update the code value with proper bounds checking
      const newCode = value.split('');

      // Ensure we have a valid array and index
      if (index >= 0 && index < length) {
        newCode[index] = char;
      }

      // Pad with empty strings if needed
      while (newCode.length < length) {
        newCode.push('');
      }

      const updatedCode = newCode.join('').slice(0, length);
      onChange(updatedCode);

      // Move to next input if character was entered
      if (char && index < length - 1) {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) {
          nextInput.focus();
        }
      }
    } catch (error) {
      console.error('Error in handleInputChange:', error);
      // Fallback: just update with the safe character
      const char = inputValue.replace(/[^a-zA-Z0-9]/g, '').slice(-1);
      if (char) {
        onChange(value.substring(0, index) + char + value.substring(index + 1));
      }
    }
  };

  const handleKeyDown = (index: number, event: KeyboardEvent) => {
    try {
      if (event.key === 'Backspace') {
        event.preventDefault();

        const newCode = value.split('');

        if (newCode[index]) {
          // Clear current digit
          newCode[index] = '';
        } else if (index > 0) {
          // Move to previous input and clear it
          newCode[index - 1] = '';
          const prevInput = inputRefs.current[index - 1];
          if (prevInput) {
            prevInput.focus();
          }
        }

        while (newCode.length < length) {
          newCode.push('');
        }

        onChange(newCode.join('').slice(0, length));
      } else if (event.key === 'ArrowLeft' && index > 0) {
        const prevInput = inputRefs.current[index - 1];
        if (prevInput) {
          prevInput.focus();
        }
      } else if (event.key === 'ArrowRight' && index < length - 1) {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) {
          nextInput.focus();
        }
      }
    } catch (error) {
      console.error('Error in handleKeyDown:', error);
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    try {
      event.preventDefault();
      const pastedData = event.clipboardData
        .getData('text')
        .replace(/[^a-zA-Z0-9]/g, '')
        .slice(0, length);
      onChange(pastedData);

      // Focus the next empty input or the last input
      const nextIndex = Math.min(pastedData.length, length - 1);
      const targetInput = inputRefs.current[nextIndex];
      if (targetInput) {
        targetInput.focus();
      }
    } catch (error) {
      console.error('Error in handlePaste:', error);
    }
  };

  // Create digits array with safety checks
  const digits = React.useMemo(() => {
    try {
      const valueArray = value ? value.split('') : [];
      const result = [];
      for (let i = 0; i < length; i++) {
        result[i] = valueArray[i] || '';
      }
      return result;
    } catch (error) {
      console.error('Error creating digits array:', error);
      return Array(length).fill('');
    }
  }, [value, length]);

  return (
    <Box>
      {/* Screen reader label */}
      <Typography
        component="label"
        variant="body2"
        sx={{
          display: 'block',
          mb: 1,
          color: error ? 'error.main' : 'text.primary',
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      {/* Code input fields */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'center',
          alignItems: 'center',
          mb: helperText ? 1 : 0,
        }}
        onPaste={handlePaste}
      >
        {/* First group of 3 characters */}
        {Array.from({ length: 3 }, (_, index) => (
          <TextField
            key={index}
            inputRef={el => (inputRefs.current[index] = el)}
            value={digits[index] || ''}
            onChange={e => handleInputChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            onFocus={() => setFocusedIndex(index)}
            onBlur={() => setFocusedIndex(null)}
            disabled={disabled}
            error={error}
            inputProps={{
              maxLength: 1,
              style: {
                textAlign: 'center',
                fontSize: '1.5rem',
                fontWeight: '600',
                padding: theme.spacing(1.5),
                textTransform: 'uppercase',
              },
              'aria-label': `Character ${index + 1} of ${length}`,
              'data-testid': `magic-code-input-${index + 1}`,
            }}
            sx={{
              width: 56,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
                '&.Mui-error': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'error.main',
                  },
                },
              },
              // Highlight focused digit with subtle background
              ...(focusedIndex === index && {
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'action.hover',
                },
              }),
            }}
          />
        ))}

        {/* Dash separator */}
        <Typography
          variant="h5"
          sx={{
            color: 'text.secondary',
            fontWeight: 'bold',
            mx: 1,
          }}
        >
          -
        </Typography>

        {/* Second group of 3 characters */}
        {Array.from({ length: 3 }, (_, index) => {
          const actualIndex = index + 3;
          return (
            <TextField
              key={actualIndex}
              inputRef={el => (inputRefs.current[actualIndex] = el)}
              value={digits[actualIndex] || ''}
              onChange={e => handleInputChange(actualIndex, e.target.value)}
              onKeyDown={e => handleKeyDown(actualIndex, e)}
              onFocus={() => setFocusedIndex(actualIndex)}
              onBlur={() => setFocusedIndex(null)}
              disabled={disabled}
              error={error}
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  padding: theme.spacing(1.5),
                  textTransform: 'uppercase',
                },
                'aria-label': `Character ${actualIndex + 1} of ${length}`,
                'data-testid': `magic-code-input-${actualIndex + 1}`,
              }}
              sx={{
                width: 56,
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                  '&.Mui-error': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'error.main',
                    },
                  },
                },
                // Highlight focused digit with subtle background
                ...(focusedIndex === actualIndex && {
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'action.hover',
                  },
                }),
              }}
            />
          );
        })}
      </Box>{' '}
      {/* Helper text */}
      {helperText && (
        <Typography
          variant="caption"
          sx={{
            color: error ? 'error.main' : 'text.secondary',
            display: 'block',
            textAlign: 'center',
            mt: 0.5,
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default CodeInputField;
